// Reine Helfer der Registrierungs-Seite (register.app.js). Unit-testbar, kein State/DOM.

export const evaluatePasswordStrength = (password = '') => {
    const value = (password || '').toString();
    let score = 0;
    if (value.length >= 8) score += 1;
    if (value.length >= 12) score += 1;
    if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
    if (/\d/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value)) score += 1;
    if (value.length < 8) {
        return {
            label: 'Zu kurz',
            hint: 'Mindestens 8 Zeichen sind Pflicht. 10+ mit Zahl und Sonderzeichen ist besser.',
            level: 'weak',
            percent: Math.max((value.length / 8) * 20, 6)
        };
    }
    if (score <= 2) {
        return {
            label: 'Schwach',
            hint: 'Nutze mehr Länge sowie Gross-/Kleinbuchstaben, Zahlen und Sonderzeichen.',
            level: 'weak',
            percent: 34
        };
    }
    if (score <= 4) {
        return {
            label: 'Okay',
            hint: 'Fast gut. Noch ein Sonderzeichen oder mehr Länge macht es deutlich stabiler.',
            level: 'okay',
            percent: 68
        };
    }
    return {
        label: 'Stark',
        hint: 'Sauber. Dieses Passwort ist deutlich robuster für den Owner-Zugang.',
        level: 'strong',
        percent: 100
    };
};

export const normalizeInviteList = (invites = []) => {
    const allowedRoles = new Set(['Admin', 'VL', 'Mitarbeiter', 'Partner', 'Accounting']);
    const byEmail = new Map();
    invites.forEach(invite => {
        const email = (invite.email || '').trim().toLowerCase();
        if (!email) return;
        const role = allowedRoles.has(invite.role) ? invite.role : 'Mitarbeiter';
        const name = (invite.name || '').trim();
        const existing = byEmail.get(email);
        if (!existing) {
            byEmail.set(email, { name, email, role });
            return;
        }
        if (!existing.name && name) {
            existing.name = name;
        }
    });
    return Array.from(byEmail.values());
};

export const getDuplicateEmails = (items = []) => {
    const duplicates = new Set();
    const seen = new Set();
    items.forEach(item => {
        const email = (item?.email || '').trim().toLowerCase();
        if (!email) return;
        if (seen.has(email)) {
            duplicates.add(email);
            return;
        }
        seen.add(email);
    });
    return Array.from(duplicates.values());
};

export const normalizeJoinRole = (raw) => {
    const value = (raw || '').toString().trim().toLowerCase();
    if (!value) return '';
    if (value.includes('admin')) return 'Admin';
    if (value === 'vl' || value.includes('manager') || value.includes('team lead') || value.includes('vertriebsleiter')) return 'VL';
    if (value.includes('accounting') || value.includes('finance')) return 'Accounting';
    if (value.includes('partner') || value.includes('vermittler') || value.includes('entwickler')) return 'Partner';
    if (value.includes('mitarbeiter') || value.includes('sales')) return 'Mitarbeiter';
    return '';
};

export const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
        code += chars[Math.floor(Math.random() * chars.length)];
    }
    return code;
};

export const normalizeRate = (raw, fallback) => {
    if (raw === null || raw === undefined || raw === '') return fallback;
    const numeric = typeof raw === 'number' ? raw : parseFloat(raw.toString().replace(',', '.'));
    if (!Number.isFinite(numeric) || numeric < 0) return fallback;
    const normalized = numeric > 1 ? numeric / 100 : numeric;
    return Math.min(Math.max(normalized, 0), 1);
};

// ─── Charge 2: Draft/Subscription/Invite/Step (pure) ───

export const resolveDraftTimestamp = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'number') return raw;
    if (raw instanceof Date) return raw.getTime();
    if (typeof raw.toMillis === 'function') return raw.toMillis();
    if (typeof raw.seconds === 'number') return raw.seconds * 1000;
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? null : parsed;
};

export const formatDraftTimestamp = (raw) => {
    const ts = resolveDraftTimestamp(raw);
    if (!ts) return '';
    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('de-CH', { dateStyle: 'medium', timeStyle: 'short' });
};

export const deriveSalesTypesFromProducts = (products) => {
    const allowedTypes = new Set(['service', 'product', 'insurance']);
    const typeSet = new Set();
    (products || []).forEach(product => {
        const type = (product.type || '').trim();
        if (allowedTypes.has(type)) {
            typeSet.add(type);
        }
    });
    return Array.from(typeSet);
};

export const normalizeSubscriptionModules = (modules) => {
    const data = modules || {};
    return {
        financePlus: Boolean(data.financePlus),
        mobileApp: Boolean(data.mobileApp),
        gamification: Boolean(data.gamification)
    };
};

export const getSelfInviteEmails = (items = [], accountEmail = '') => {
    const normalizedAccountEmail = (accountEmail || '').trim().toLowerCase();
    if (!normalizedAccountEmail) return [];
    return items
        .map(item => (item?.email || '').trim().toLowerCase())
        .filter(email => email && email === normalizedAccountEmail);
};

export const stepLabel = (step) => {
    const labels = {
        mode: 'Registrierungsart',
        account: 'Konto',
        company: 'Firma',
        features: 'Pläne & Team',
        pipeline: 'Pipeline',
        products: 'Produkte',
        team: 'Team',
        join: 'Firmencode',
        summary: 'Zusammenfassung'
    };
    return labels[step] || step;
};

// ─── Charge 3: Validierung/Normalisierung/Addon/Draft (pure) ───

export const isValidEmail = (value) => {
    const email = (value ?? '').toString().trim();
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const normalizeWebsite = (raw) => {
    const trimmed = (raw || '').toString().trim();
    if (!trimmed) return '';
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
};

export const normalizeEmployeeCount = (value, planKey) => {
    const parsed = parseInt(value ?? '', 10);
    const minSeats = 1;
    const safeValue = Number.isFinite(parsed) ? parsed : minSeats;
    return Math.max(minSeats, safeValue);
};

export const normalizeMoney = (raw) => {
    if (raw === null || raw === undefined || raw === '') return null;
    const numeric = typeof raw === 'number' ? raw : parseFloat(raw.toString().replace(',', '.'));
    return Number.isFinite(numeric) ? numeric : null;
};

export const formatCurrency = (value, currency = 'CHF') => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return `${currency} 0`;
    return `${currency} ${numeric.toFixed(0)}`;
};

export const getSessionBlockedMessage = (user) => {
    const label = user?.email || 'deinem aktuellen Konto';
    return `Du bist bereits eingeloggt als ${label}. Für die Registrierung bitte ausloggen oder den Link in einem privaten Fenster öffnen.`;
};

export const normalizeBillingCycle = (value) => {
    const normalized = (value || '').toString().trim().toLowerCase();
    return normalized === 'year' || normalized === 'annual' ? 'year' : 'month';
};

export const getEnabledAddonCount = (modules) => {
    const data = normalizeSubscriptionModules(modules);
    return Object.values(data).filter(Boolean).length;
};

export const getAddonSeatBreakdown = (seatCount) => {
    const totalSeats = Math.max(1, parseInt(seatCount ?? '', 10) || 1);
    const tier1Seats = Math.min(totalSeats, 3);
    const tier2Seats = Math.max(totalSeats - 3, 0);
    return { totalSeats, tier1Seats, tier2Seats };
};

export const pickLatestDraft = (localDraft, remoteDraft) => {
    if (!localDraft && !remoteDraft) return null;
    if (!localDraft) return remoteDraft;
    if (!remoteDraft) return localDraft;
    const localTs = resolveDraftTimestamp(localDraft.updatedAt);
    const remoteTs = resolveDraftTimestamp(remoteDraft.updatedAt);
    if (remoteTs && (!localTs || remoteTs >= localTs)) {
        return remoteDraft;
    }
    return localDraft;
};
